<template>
    <span>
        <app-button
            v-test-id="`show_hide_columns-button`"
            secondary
            icon-left="show"
            :text="$t('show_hide_columns')"
            :disabled="disabled"
            @click="active = true"
        />

        <app-dialog
            v-model="active"
            :disabled="disabled"
            size="large"
            :persistent="false"
            :overline="$t('column_visibility')"
            :title="$t('show_hide_columns')"
            :text="$t('select_the_columns_you_would_like_to_display')"
        >
            <template #header-actions>
                <v-simple-checkbox
                    v-test-id="`show_hide_columns-select_all-checkbox`"
                    :value="indeterminate || complete"
                    :indeterminate="indeterminate"
                    class="ml-2"
                    color="primaryText"
                    hide-details
                    @click="selectAll"
                />
            </template>

            <template #default>
                <v-list>
                    <v-list-item
                        v-for="item in selectable"
                        :key="item.value"
                        v-test-id="`show_hide_columns-${item.value}-list-item`"
                        :value="item.value"
                        class="px-6"
                        @click="setColumnVisibility(item)"
                    >
                        <v-list-item-content>
                            <v-list-item-title class="primaryText--text">
                                {{ item.text }}
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-icon v-if="item.visible" color="primaryBackground">
                                {{ getIcon('success') }}
                            </v-icon>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>
            </template>

            <template #actions>
                <v-spacer />
                <app-button
                    v-test-id="`show_hide_columns-button-close`"
                    secondary
                    :text="$t('close')"
                    @click="active = false"
                />
                <app-button
                    v-test-id="`show_hide_columns-button-apply`"
                    :text="$t('apply')"
                    class="ml-2"
                    @click="applyColumnVisibility"
                />
            </template>
        </app-dialog>
    </span>
</template>

<script>
export default {
    name: 'ColumnVisibilityButton',
    components: {
        AppDialog: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppDialog'
            )
    },
    props: {
        /**
         * AppTable->activeHeaderKeys
         */
        value: {
            type: Array,
            default: () => []
        },
        headers: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        active: false,
        items: []
    }),
    computed: {
        selectable() {
            return this.items.filter(item => item.selectable);
        },
        visible() {
            return this.items.filter(item => item.visible);
        },
        complete() {
            return this.selectable.every(item => item.visible);
        },
        indeterminate() {
            return this.selectable.some(item => item.visible) && !this.complete;
        },
        persistent() {
            return this.headers.filter(header => header.active && header.persistent).map(header => header.column);
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(value) {
                if (!value || !value.length) {
                    return;
                }

                if (this.headers) {
                    const items = this.headers
                        .filter(header => !header.persistent)
                        .map(header => {
                            const item = this.items.find(itm => itm.value === header.column);

                            let visible = item !== undefined ? item.visible : null;

                            if (visible === null) {
                                visible = value.includes(header.column);
                            }

                            return {
                                value: header.column,
                                text: header.text,
                                selectable: header.active && header.text ? true : false,
                                visible
                            };
                        });

                    this.items = items;
                } else {
                    this.items = [];
                }
            }
        }
    },
    methods: {
        selectAll() {
            if (this.complete) {
                this.items.forEach(item => {
                    item.visible = false;
                });

                this.items[0].visible = true;
            } else {
                this.items.forEach(item => {
                    if (item.selectable) {
                        item.visible = true;
                    }
                });
            }
        },
        setColumnVisibility(item) {
            const isVisible = item.visible ? true : false;

            if (isVisible && this.visible.length <= 1) {
                return;
            }

            this.items = this.items.map(itm => {
                if (itm.value === item.value) {
                    itm.visible = !isVisible;
                }
                return itm;
            });
        },
        applyColumnVisibility() {
            this.active = false;

            const columns = this.visible.map(item => item.value).concat(this.persistent);
            this.$emit('input', [...new Set(columns)]);
        }
    }
};
</script>
